import { type Getter, atom } from "jotai";
import { atomFamily, loadable } from "jotai/utils";

import { Logger } from "@moment/logging";

import { $isAuthenticated } from "../../authenication";
import { $collabSessionsStore } from "../../document-collab-registry";
import { env, getPublishWebUrl } from "../../env/env";
import { $gitBranches, $gitRemotes, $gitStatus } from "../../git";
import { $hasSharePermission, $isDocumentPublished, $remoteWorkspace } from "../sharing";
import { $pendingPermissionsAtom } from "../sharing/pending-permissions.atom";
import { $remoteDocumentMetadataExists } from "./docsapi.atom";
import { $syncState } from "./sync-state.atom";
import type { PlanStatus, PlannedOutcome, PublishStatus, SyncSnapshot } from "./sync.types";

const GIT_PUBLISHING_BRANCH_NAME = "main";
const GIT_COLLAB_BRANCH = "collab/main";
const GIT_PUBLISHING_REMOTE_NAME = "moment";

const logger = new Logger("currentStateWorkflow");

/*
    This gets the current state of the document from a sharing/collab editing perspective.
    This workflow DOES NOT MUTATE any state. It just reads the current state
    and reports back what it sees in that snapshot.
    Checks the following:
        - Git State
            - git status
            - git branches
            - git remotes (and whether a remote exists)
            - is repo clean?
            - is repo ahead?
        - is published?
        - sharing permissions and whether the document is shared.
        - collab state
*/
export const $syncCurrentSnapshot = atomFamily((documentId: string | undefined) => {
	const snapshotAtom = atom(async (get): Promise<SyncSnapshot> => {
		// STEP 0: BASICS
		const isAuthenticated = get($isAuthenticated);

		// STEP 1: FETCH ALL GIT DATA
		const gitStatus = await get($gitStatus(documentId));
		const branches = (await get($gitBranches(documentId))) ?? [];
		const remotes = (await get($gitRemotes(documentId))) ?? [];
		// Check if document remote is set (moment remote exists)
		// Note: In the future, we could validate the remote URL points to docsApiUrl
		const momentRemote = remotes?.find((remote) => remote.name === GIT_PUBLISHING_REMOTE_NAME);
		const momentRemoteUrl = momentRemote?.url;
		const documentRemoteSet =
			remotes?.find((remote) => remote.name === GIT_PUBLISHING_REMOTE_NAME) !== undefined;

		// Check if we're on the publishing branch
		const isOnPublishBranch =
			(gitStatus && gitStatus.branch === GIT_PUBLISHING_BRANCH_NAME) || !gitStatus;

		const isOnCollabBranch =
			(gitStatus && gitStatus.branch === GIT_COLLAB_BRANCH) || !gitStatus;

		// Check if tracking branch is set
		const publishingTrackingBranchSet = !!branches?.find(
			(branch) => branch.name === GIT_PUBLISHING_BRANCH_NAME
		)?.upstream;

		const collabTrackingBranchSet = !!branches?.find(
			(branch) => branch.name === GIT_COLLAB_BRANCH
		)?.upstream;

		const branch = gitStatus?.branch ?? "";
		const ahead = gitStatus?.ahead ?? 0;
		const behind = gitStatus?.behind ?? 0;
		const repoClean = gitStatus?.isClean ?? false;
		const repoAhead = ahead > 0;
		const gitRepoStatus = !!gitStatus;

		// STEP 2: FETCH ALL PERMISSIONS DATA
		const remoteRepoExists = (await get($remoteDocumentMetadataExists(documentId))) ?? false;
		const isPublished = (await get($isDocumentPublished(documentId))) ?? false;
		const hasSharePermission = (await get($hasSharePermission(documentId))) ?? false;
		const publishWorkspace = await get($remoteWorkspace(documentId));
		const syncState = get($syncState(documentId));

		// STEP 3: CHECK COLLAB INITIALIZATION STATUS
		let collabInitialized = false;
		if (publishWorkspace && documentId) {
			const collabSessions = get($collabSessionsStore(publishWorkspace.slug));
			const documentSessions = collabSessions[documentId];
			collabInitialized =
				documentSessions !== undefined && Object.keys(documentSessions.sessions).length > 0;
		}

		// STEP 4: DERIVE PUBLISH STATUS
		let publishStatus: PublishStatus;
		if (remoteRepoExists === false) {
			publishStatus = "unpublished";
		} else if (remoteRepoExists === true && isPublished === false) {
			publishStatus = "unpublished";
		} else if (remoteRepoExists === true && isPublished === true) {
			publishStatus = "published";
		} else if (syncState.currentWorkflow === "publish") {
			publishStatus = "publishing";
		} else {
			publishStatus = "status-pending";
		}

		let publishUrl = undefined;
		if (publishWorkspace && publishStatus === "published") {
			const webUrl = getPublishWebUrl(env.appEnv ?? "staging");
			publishUrl = `${webUrl}/d/${publishWorkspace.name}/${documentId}`;
		}

		return {
			documentId,
			publishWorkspace,

			branch,
			branches,
			ahead,
			behind,

			remoteRepoExists, // this is true if the remote exists in gitea
			documentRemoteSet, // this is true if remote is set in local git
			momentRemoteUrl, // url to the moment remote or undefined,

			isAuthenticated,
			gitRepoStatus,
			publishingTrackingBranchSet,
			collabTrackingBranchSet,
			repoClean,
			repoAhead,
			isOnCollabBranch,
			isOnPublishBranch,
			// this returns the current publish state
			publishStatus,
			hasSharePermission,
			isPublished,
			publishUrl,
			collabInitialized,

			// we do not query for base tables yet.
			hasBaseTables: false,
		};
	});
	snapshotAtom.debugLabel = `syncCurrentSnapshot/${documentId}`;
	return snapshotAtom;
});

export const $loadableSyncCurrentSnapshot = atomFamily((documentId: string | undefined) =>
	loadable($syncCurrentSnapshot(documentId))
);

export interface PlannedOutcomeResult {
	canExecute: boolean;
	plannedOutcome: PlannedOutcome;
	planStatus: PlanStatus;
}

function handlePermissions(prevPlanOutcome: PlannedOutcome, get: Getter): PlannedOutcome {
	const pendingPermissions = get($pendingPermissionsAtom);
	let plannedOutcome: PlannedOutcome = prevPlanOutcome;
	if (pendingPermissions.size > 0 && plannedOutcome === null) {
		plannedOutcome = "update-permissions";
	}
	return plannedOutcome;
}

function handleRemotes(
	prevPlanOutcome: PlannedOutcome,
	shouldAutoPush: boolean,
	shouldAutoPublish: boolean,
	snapshot: SyncSnapshot
): PlannedOutcome {
	if (!snapshot.isAuthenticated) {
		return "commit";
	}

	let plannedOutcome: PlannedOutcome = prevPlanOutcome;

	// next, should we attempt to go upstream?
	// within this block, we are on the publishing branch (or what will become it)
	if (shouldAutoPush || shouldAutoPublish) {
		if (
			// These 3 represent that we initialized the upstream new, so we have to push
			!snapshot.gitRepoStatus ||
			!snapshot.documentRemoteSet ||
			!snapshot.collabTrackingBranchSet ||
			!snapshot.publishingTrackingBranchSet ||
			// these 2 states represent that we have changes to send upstream
			!snapshot.repoClean ||
			snapshot.repoAhead
		) {
			plannedOutcome = "push";
		}
	}
	return plannedOutcome;
}

// Below calculates what the expected outcome is if we to complete the workflow.

export const calculatePlanStatus = (
	snap: SyncSnapshot,
	plannedOutcome: PlannedOutcome
): PlanStatus => {
	let planStatus: PlanStatus = "achievable";
	if (!snap.isAuthenticated && plannedOutcome !== "commit") {
		planStatus = "unachievable-not-authenticated";
	} else if (snap.gitRepoStatus && !(snap.isOnPublishBranch || snap.isOnCollabBranch)) {
		planStatus = "unachievable-not-on-synced-branch";
	} else if (snap.gitRepoStatus && snap.behind > 0) {
		planStatus = "unachievable-behind-remote";
	} else if (plannedOutcome === null) {
		planStatus = "unachievable-nothing-to-do";
	}
	return planStatus;
};

export const canExecuteSync = (planStatus: PlanStatus, plannedOutcome: PlannedOutcome): boolean => {
	if (plannedOutcome === null) {
		return false;
	}

	return (
		planStatus === "achievable" ||
		// Allow commits even if not authenticated or not on sync branch
		((planStatus === "unachievable-not-authenticated" ||
			planStatus === "unachievable-not-on-synced-branch") &&
			plannedOutcome === "commit")
	);
};

export const $plannedOutcomePublish = atomFamily((documentId: string | undefined) => {
	const outcomeAtom = atom(async (get) => {
		let plannedOutcome: PlannedOutcome = null;
		const snap = await get($syncCurrentSnapshot(documentId));
		const shouldAutoPush = snap.isOnPublishBranch === true && snap.remoteRepoExists === true;
		plannedOutcome = handleRemotes(plannedOutcome, shouldAutoPush, false, snap);
		plannedOutcome = handlePermissions(plannedOutcome, get);

		// if there is no remote, or the document is not published,
		// we need to publish
		if (snap.remoteRepoExists === false || snap.isPublished === false) {
			plannedOutcome = "publish"; // This plan results in a publish
		}

		const planStatus = calculatePlanStatus(snap, plannedOutcome);

		return {
			canExecute: canExecuteSync(planStatus, plannedOutcome),
			planStatus,
			plannedOutcome,
		};
	});
	outcomeAtom.debugLabel = `plannedOutcomePublish/${documentId}`;
	return outcomeAtom;
});

export const $plannedOutcomeShareSync = atomFamily((documentId: string | undefined) => {
	const outcomeAtom = atom(async (get) => {
		let plannedOutcome: PlannedOutcome = null;
		const snap = await get($syncCurrentSnapshot(documentId));
		const shouldAutoPush = snap.isOnCollabBranch && snap.remoteRepoExists === true;
		if (!snap.gitRepoStatus || !snap.repoClean) {
			logger.debug("Setting planned outcome to commit");
			plannedOutcome = "commit";
		}

		if (!snap.remoteRepoExists) {
			logger.debug("Setting planned outcome to push");
			plannedOutcome = "push";
		}

		plannedOutcome = handleRemotes(plannedOutcome, shouldAutoPush, false, snap);
		logger.debug("handle remotes resulted in planned outcome", { plannedOutcome });
		plannedOutcome = handlePermissions(plannedOutcome, get);
		logger.debug("handle permissions resulted in planned outcome", { plannedOutcome });
		const planStatus = calculatePlanStatus(snap, plannedOutcome);
		return {
			canExecute: canExecuteSync(planStatus, plannedOutcome) && snap.documentId !== undefined,
			planStatus,
			plannedOutcome,
		};
	});
	outcomeAtom.debugLabel = `plannedOutcomeShareSync/${documentId}`;
	return outcomeAtom;
});

export const $plannedOutcomePublishIfUnpublished = atomFamily((documentId: string | undefined) => {
	const outcomeAtom = atom(async (get) => {
		let plannedOutcome: PlannedOutcome = null;
		const snap = await get($syncCurrentSnapshot(documentId));
		const shouldAutoPublish = snap.isOnPublishBranch === true;
		const shouldAutoPush = snap.isOnPublishBranch === true && snap.remoteRepoExists === true;
		plannedOutcome = handleRemotes(plannedOutcome, shouldAutoPush, shouldAutoPublish, snap);
		plannedOutcome = handlePermissions(plannedOutcome, get);

		if (snap.remoteRepoExists === false || snap.isPublished === false) {
			plannedOutcome = "publish"; // This plan results in a publish
		}
		const planStatus = calculatePlanStatus(snap, plannedOutcome);
		return {
			canExecute: canExecuteSync(planStatus, plannedOutcome),
			planStatus,
			plannedOutcome,
		};
	});
	outcomeAtom.debugLabel = `plannedOutcomePublishIfUnpublished/${documentId}`;
	return outcomeAtom;
});
